import type { KeyboardEvent, RefObject } from 'react'

const FIRST_STOP =
  '[tabindex="0"], button:not([disabled]), input:not([disabled])'

export interface PanelTabBridge {
  // On the summary line, in the capture phase.
  onLineKeyDownCapture: (event: KeyboardEvent) => void
  // On the grid's popover content.
  onGridKeyDown: (event: KeyboardEvent) => void
}

// The open grid sits in a portal outside the note's tab order. Tab from
// the summary line goes into the grid, and Shift+Tab from the grid's first
// stop goes back. When the node has an action strip, Done hands Tab on to
// it and Shift+Tab returns; otherwise Tab is left to the browser.
export function usePanelTabBridge(
  open: boolean,
  line: RefObject<HTMLElement | null>,
  content: RefObject<HTMLElement | null>,
  focusLine: () => void
): PanelTabBridge {
  const onLineKeyDownCapture = (event: KeyboardEvent): void => {
    if (!open || event.key !== 'Tab') {
      return
    }
    const inStrip = (event.target as Element).closest(
      '[data-observation-actions]'
    )
    if (inStrip && event.shiftKey) {
      const done =
        content.current?.querySelector<HTMLElement>('[data-panel-done]')
      if (done) {
        event.preventDefault()
        done.focus()
      }
      return
    }
    if (
      !inStrip &&
      !event.shiftKey &&
      !content.current?.contains(event.target as Node)
    ) {
      const first = content.current?.querySelector<HTMLElement>(FIRST_STOP)
      if (first) {
        event.preventDefault()
        first.focus()
      }
    }
  }

  const onGridKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Tab') {
      return
    }
    const target = event.target as HTMLElement
    if (!event.shiftKey && target.dataset.panelDone !== undefined) {
      const strip = line.current?.querySelector<HTMLElement>(
        '[data-observation-actions] [tabindex="0"]'
      )
      if (strip) {
        event.preventDefault()
        strip.focus()
      }
      return
    }
    const first = content.current?.querySelector<HTMLElement>(FIRST_STOP)
    if (event.shiftKey && target === first) {
      event.preventDefault()
      focusLine()
    }
  }

  return { onLineKeyDownCapture, onGridKeyDown }
}
